import { MinorBadge } from "@/components/contractings/pills";
import dayjs from "dayjs";

import { cn } from "@/lib/utils";
import { formatMoney, formatMoneyCompact, toAmount } from "@/lib/format";
import { STATUS_LIST } from "@/lib/domain-constants";
import { MemberAvatar } from "@/components/team/AssigneeSelect";
import { MonthComparison } from "@/components/team/MonthComparison";
import type {
  ContractingItem,
  MonthlyStat,
  TeamMember,
} from "@/types/domain";

const OPEN_STATUSES = [0, 1, 2]; // Sin estado, Revisada, Negociando: lo que sigue vivo.

function Card({
  eyebrow,
  children,
}: {
  eyebrow: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-2xl border bg-card p-4">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
        {eyebrow}
      </p>
      {children}
    </div>
  );
}

export function amountOf(item: ContractingItem) {
  return toAmount(item.contracting_contractamount);
}

/**
 * La siguiente etapa del cronograma de SEACE que todavía no vence. NO es el cierre
 * del proceso: puede ser "Presentación de ofertas", "Absolución de consultas" o la
 * que toque. Es el próximo plazo que el comercial no puede dejar pasar.
 */
export function nextDeadline(
  item: ContractingItem,
): { etapa: string; days: number } | null {
  const next = (item.contractingversion_schedule ?? [])
    .map((stage) => ({ etapa: stage.etapa, date: dayjs(stage.fechaFin) }))
    .filter((stage) => stage.date.isValid() && stage.date.isAfter(dayjs()))
    .sort((a, b) => a.date.valueOf() - b.date.valueOf())[0];

  return next
    ? { etapa: next.etapa, days: next.date.diff(dayjs(), "day") }
    : null;
}

/** Barra apilada con el peso de cada estado sobre el total en juego. */
function StageBar({ items }: { items: ContractingItem[] }) {
  const open = items.filter((item) =>
    OPEN_STATUSES.includes(item.contractinguser?.contractinguser_status ?? 0),
  );
  const total = open.reduce((sum, item) => sum + amountOf(item), 0);

  const byStatus = OPEN_STATUSES.map((status) => {
    const meta = STATUS_LIST.find((s) => s.value === status)!;
    const amount = open
      .filter(
        (item) => (item.contractinguser?.contractinguser_status ?? 0) === status,
      )
      .reduce((sum, item) => sum + amountOf(item), 0);
    return { meta, amount };
  });

  return (
    <>
      <p className="font-mono text-2xl font-extrabold tracking-tight">
        {formatMoneyCompact(total)}
      </p>
      <div className="flex h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        {byStatus.map(({ meta, amount }) =>
          amount > 0 ? (
            <div
              key={meta.value}
              style={{
                width: `${(amount / total) * 100}%`,
                backgroundColor: meta.color,
              }}
            />
          ) : null,
        )}
      </div>
      <div className="flex flex-wrap gap-3">
        {byStatus.map(({ meta, amount }) => (
          <span
            key={meta.value}
            className="flex items-center gap-1.5 text-[11px] text-slate-500"
          >
            <span
              className="size-2 rounded-sm"
              style={{ backgroundColor: meta.color }}
            />
            {meta.label}
            <span className="font-mono font-bold text-slate-700 dark:text-slate-200">
              {amount > 0 ? formatMoneyCompact(amount) : 0}
            </span>
          </span>
        ))}
      </div>
    </>
  );
}

/** Las cuatro señales del que dirige: dinero, carga, cierres y tendencia. */
export function TeamStats({
  items,
  members,
  segmentLabel,
  monthStat,
  monthLabel,
  monthlyStats,
  month,
  monthsLoading,
}: {
  items: ContractingItem[];
  members: TeamMember[];
  segmentLabel: string;
  monthlyStats: MonthlyStat[];
  month: string;
  monthsLoading?: boolean;
  /** Los cierres del mes elegido. Vienen del backend: contarlos sobre la página
   *  cargada daría el número de la página, no el del mes. */
  monthStat?: MonthlyStat;
  monthLabel: string;
}) {
  const byUser = new Map<string, ContractingItem[]>();
  let unassigned = 0;
  for (const item of items) {
    const assigned = item.contractinguser?.assignedUserId ?? null;
    if (!assigned) unassigned++;
    else byUser.set(assigned, [...(byUser.get(assigned) ?? []), item]);
  }

  const heaviest = [...byUser.entries()]
    .map(([userId, list]) => ({
      member: members.find((m) => m.userId === userId),
      list,
      amount: list.reduce((sum, item) => sum + amountOf(item), 0),
    }))
    .filter((row) => row.member)
    .sort((a, b) => b.amount - a.amount)[0];

  // Los cierres del mes elegido salen del backend; el resto de las tarjetas mira
  // el pipeline vivo que ya está cargado.
  const won = monthStat?.won ?? 0;
  const lost = monthStat?.lost ?? 0;
  const wonAmount = monthStat?.wonAmount ?? 0;
  const winRate = won + lost > 0 ? Math.round((won / (won + lost)) * 100) : null;

  return (
    <div className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      <Card eyebrow={`En juego por etapa · ${segmentLabel}`}>
        <StageBar items={items} />
      </Card>

      <Card eyebrow={`Carga más alta · ${segmentLabel}`}>
        {heaviest?.member ? (
          <>
            <div className="flex items-center gap-2">
              <MemberAvatar
                member={{
                  id: heaviest.member.userId,
                  name: heaviest.member.name,
                  image: heaviest.member.image,
                }}
                className="size-7"
              />
              <span className="text-base font-bold">
                {heaviest.member.name}
              </span>
            </div>
            <p className="text-sm text-slate-500">
              {formatMoneyCompact(heaviest.amount)} · {heaviest.list.length}{" "}
              licitación{heaviest.list.length === 1 ? "" : "es"} activa
              {heaviest.list.length === 1 ? "" : "s"}
            </p>
          </>
        ) : (
          <p className="text-sm text-slate-400">Nadie tiene nada asignado aún.</p>
        )}
        {unassigned > 0 && (
          <p className="text-xs font-semibold text-red-600">
            {unassigned} sin responsable por repartir
          </p>
        )}
      </Card>

      <Card eyebrow={`Cerradas en ${monthLabel.toLowerCase()} · equipo`}>
        <div className="flex items-baseline gap-4">
          <span className="text-2xl font-extrabold text-emerald-600">
            {won}
            <span className="ml-1 text-sm font-semibold text-slate-500">
              ganadas
            </span>
          </span>
          <span className="text-2xl font-extrabold text-red-600">
            {lost}
            <span className="ml-1 text-sm font-semibold text-slate-500">
              perdidas
            </span>
          </span>
        </div>
        <p className="text-xs text-slate-400">
          {won + lost === 0
            ? `Aún sin cierres registrados en ${monthLabel.toLowerCase()}.`
            : `${winRate}% de efectividad${
                wonAmount > 0 ? ` · ${formatMoneyCompact(wonAmount)} ganados` : ""
              }.`}
        </p>
      </Card>

      <MonthComparison
        stats={monthlyStats}
        month={month}
        isLoading={monthsLoading}
        scopeLabel="equipo"
      />
    </div>
  );
}

/** Las cuatro señales del comercial: su cartera, su cierre y lo que puede tomar. */
export function MyStats({
  mine,
  inMyLines,
}: {
  mine: ContractingItem[];
  inMyLines: ContractingItem[];
}) {
  const nextClose = mine
    .map((item) => ({ item, deadline: nextDeadline(item) }))
    .filter((row) => row.deadline !== null)
    .sort((a, b) => a.deadline!.days - b.deadline!.days)[0];

  const free = inMyLines.filter(
    (item) => !item.contractinguser?.assignedUserId,
  );

  return (
    <div className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      <Card eyebrow="Mi cartera · solo mío">
        <StageBar items={mine} />
      </Card>

      <Card eyebrow="Mi próximo plazo">
        {nextClose ? (
          <>
            <p
              className="text-2xl font-extrabold text-amber-600"
              title={nextClose.deadline!.etapa}
            >
              {nextClose.deadline!.days}
              <span className="ml-1 text-sm font-semibold text-slate-500">
                días
              </span>
            </p>
            <p className="truncate text-xs text-slate-500">
              {nextClose.item.contracting_description ??
                nextClose.item.contracting_nomenclature}
            </p>
            {nextClose.item.contracting_minorid && (
              <MinorBadge className="mt-1 inline-block" />
            )}
            <p className="font-mono text-xs text-slate-400">
              {formatMoney(nextClose.item.contracting_contractamount)}
            </p>
          </>
        ) : (
          <p className="text-sm text-slate-400">
            Nada con fecha de cierre próxima.
          </p>
        )}
      </Card>

      <Card eyebrow="Mi seguimiento">
        <p className="text-2xl font-extrabold">
          {mine.length}
          <span className="ml-2 text-sm font-semibold text-slate-500">
            en cartera
          </span>
        </p>
        <p className="text-xs text-slate-400">
          Las que tienes asignadas ahora mismo.
        </p>
      </Card>

      <Card eyebrow="Disponibles en mis líneas">
        <p
          className={cn(
            "text-2xl font-extrabold",
            free.length > 0 ? "text-emerald-600" : "text-slate-700",
          )}
        >
          {free.length}
        </p>
        <p className="text-xs text-slate-400">
          {free.length === 0
            ? "Todo lo de tus líneas ya tiene responsable."
            : "Sin responsable · puedes tomarlas"}
        </p>
      </Card>
    </div>
  );
}
